import TopNavBar from "app/layout/TopNavBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SubscriptionService from "app/services/SubscriptionServices";

function toDateStr(date) {
    return date.toISOString().substring(0, 10);
}

function defaultRange() {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 3);
    return { start: toDateStr(start), end: toDateStr(end) };
}

function DocumentDashboard() {
    const cellStyle = { border: "1px solid #ddd", padding: "8px", fontSize: "0.85em" };
    const thStyle = { ...cellStyle, fontWeight: "bold", background: "#f5f5f5" };

    const range = defaultRange();
    const [startDate, setStartDate] = useState(range.start);
    const [endDate, setEndDate] = useState(range.end);
    const [pendingStart, setPendingStart] = useState(range.start);
    const [pendingEnd, setPendingEnd] = useState(range.end);
    const [rawData, setRawData] = useState([]);
    const [trialSubIds, setTrialSubIds] = useState(new Set()); // subscriptionIds that belong to Trial tier
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [trendView, setTrendView] = useState("monthly");  // "monthly" | "weekly"
    const [trialFilter, setTrialFilter] = useState("all");  // "all" | "exclude" | "only"
    const subscriptionService = SubscriptionService();
    const navigate = useNavigate();

    const fetchData = async (start, end) => {
        setLoading(true);
        setError(null);
        try {
            const [docData, subData] = await Promise.all([
                subscriptionService.getDocumentStats({ startDate: start, endDate: end }),
                subscriptionService.getAllSubscriptions({ page: 1, pageSize: 5000 }),
            ]);
            setRawData(Array.isArray(docData) ? docData : []);
            const trialIds = new Set(
                (subData?.data || [])
                    .filter(s => s.package_id === "1-Trial")
                    .map(s => s.id)
            );
            setTrialSubIds(trialIds);
            setStartDate(start);
            setEndDate(end);
        } catch (e) {
            setError("Failed to load data. Please try again.");
            setRawData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(startDate, endDate); }, []);

    const handleApply = () => {
        if (pendingStart && pendingEnd && pendingEnd >= pendingStart) {
            fetchData(pendingStart, pendingEnd);
        }
    };

    const metrics = useMemo(() => {
        if (!rawData.length) return null;

        // Apply trial filter
        const data = rawData.filter(d => {
            if (trialFilter === "exclude") return !trialSubIds.has(d.subscriptionId);
            if (trialFilter === "only")    return trialSubIds.has(d.subscriptionId);
            return true;
        });

        if (!data.length) return { empty: true };

        const horoscopes = data.filter(d => d.fileType === 1);
        const matches    = data.filter(d => d.fileType === 2);

        const start = new Date(startDate);
        const end   = new Date(endDate);

        // Monthly buckets
        const monthlyLabels = [];
        const monthlyHoro   = [];
        const monthlyMatch  = [];
        const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
        while (cursor <= end) {
            const y = cursor.getFullYear();
            const m = cursor.getMonth();
            monthlyLabels.push(cursor.toLocaleString('default', { month: 'short', year: 'numeric' }));
            monthlyHoro.push(horoscopes.filter(d => {
                const cd = new Date(d.createdOn);
                return cd.getFullYear() === y && cd.getMonth() === m;
            }).length);
            monthlyMatch.push(matches.filter(d => {
                const cd = new Date(d.createdOn);
                return cd.getFullYear() === y && cd.getMonth() === m;
            }).length);
            cursor.setMonth(cursor.getMonth() + 1);
        }

        // Weekly buckets
        const weeklyLabels = [];
        const weeklyHoro   = [];
        const weeklyMatch  = [];
        const startOfRange = new Date(start);
        const dow = startOfRange.getDay();
        startOfRange.setDate(startOfRange.getDate() - (dow === 0 ? 6 : dow - 1));
        startOfRange.setHours(0, 0, 0, 0);
        const weekCursor = new Date(startOfRange);
        while (weekCursor <= end) {
            const weekEnd = new Date(weekCursor);
            weekEnd.setDate(weekCursor.getDate() + 7);
            weeklyLabels.push(weekCursor.toLocaleString('default', { month: 'short', day: 'numeric' }));
            weeklyHoro.push(horoscopes.filter(d => { const cd = new Date(d.createdOn); return cd >= weekCursor && cd < weekEnd; }).length);
            weeklyMatch.push(matches.filter(d => { const cd = new Date(d.createdOn); return cd >= weekCursor && cd < weekEnd; }).length);
            weekCursor.setDate(weekCursor.getDate() + 7);
        }

        return {
            total: data.length,
            horoscopeCount: horoscopes.length,
            matchCount: matches.length,
            monthlyLabels, monthlyHoro, monthlyMatch,
            weeklyLabels, weeklyHoro, weeklyMatch,
        };
    }, [rawData, trialFilter, trialSubIds, startDate, endDate]);

    const toggleBtnStyle = (active) => ({
        padding: '4px 12px', fontSize: '0.8em', fontWeight: 500,
        background: active ? '#1976d2' : '#e0e0e0',
        color: active ? '#fff' : '#444',
        border: 'none', borderRadius: 4, cursor: 'pointer',
    });

    const filterBtnStyle = (active) => ({
        padding: '5px 14px', fontSize: '0.85em', fontWeight: 500,
        background: active ? '#7b1fa2' : '#e0e0e0',
        color: active ? '#fff' : '#333',
        border: 'none', borderRadius: 4, cursor: 'pointer',
    });

    const trendLabels = trendView === "weekly" ? metrics?.weeklyLabels : metrics?.monthlyLabels;
    const trendHoro   = trendView === "weekly" ? metrics?.weeklyHoro   : metrics?.monthlyHoro;
    const trendMatch  = trendView === "weekly" ? metrics?.weeklyMatch  : metrics?.monthlyMatch;

    return (
        <DashboardLayout>
            <div>
                <TopNavBar />

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                    <h2 style={{ margin: 0 }}>Document Creation Dashboard</h2>
                    <button type="button" onClick={() => navigate("/admin/subscribers/dashboard")}
                        style={{ padding: '6px 16px', background: '#555', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 500, cursor: 'pointer' }}>
                        Subscription Dashboard
                    </button>
                </div>

                {/* Date range filter */}
                <Card>
                    <MDBox px={2} py={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <span style={{ fontSize: '0.85em', color: '#555', fontWeight: 500 }}>Date Range:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <label style={{ fontSize: '0.8em', color: '#666' }}>From</label>
                            <input type="date" value={pendingStart}
                                onChange={e => setPendingStart(e.target.value)}
                                style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.85em' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <label style={{ fontSize: '0.8em', color: '#666' }}>To</label>
                            <input type="date" value={pendingEnd}
                                onChange={e => setPendingEnd(e.target.value)}
                                style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.85em' }} />
                        </div>
                        <button type="button" onClick={handleApply} disabled={loading}
                            style={{ padding: '6px 18px', background: loading ? '#90caf9' : '#1976d2', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 500, cursor: loading ? 'default' : 'pointer' }}>
                            {loading ? 'Loading...' : 'Apply'}
                        </button>
                        {[{ label: '1M', months: 1 }, { label: '3M', months: 3 }, { label: '6M', months: 6 }, { label: '1Y', months: 12 }].map(({ label, months }) => (
                            <button key={label} type="button"
                                onClick={() => {
                                    const e = toDateStr(new Date());
                                    const s = new Date(); s.setMonth(s.getMonth() - months);
                                    const sStr = toDateStr(s);
                                    setPendingStart(sStr); setPendingEnd(e);
                                    fetchData(sStr, e);
                                }}
                                style={{ padding: '4px 10px', background: '#e8f4fd', color: '#1976d2', border: '1px solid #90caf9', borderRadius: 4, cursor: 'pointer', fontSize: '0.8em', fontWeight: 500 }}>
                                {label}
                            </button>
                        ))}
                        {error && <span style={{ color: '#d32f2f', fontSize: '0.85em' }}>{error}</span>}
                    </MDBox>
                </Card>

                {/* Trial tier filter */}
                {!loading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
                        <span style={{ fontSize: '0.85em', color: '#555', marginRight: 4 }}>Trial tier:</span>
                        <button type="button" style={filterBtnStyle(trialFilter === "all")}     onClick={() => setTrialFilter("all")}>All</button>
                        <button type="button" style={filterBtnStyle(trialFilter === "exclude")} onClick={() => setTrialFilter("exclude")}>Exclude Trial</button>
                        <button type="button" style={filterBtnStyle(trialFilter === "only")}    onClick={() => setTrialFilter("only")}>Trial Only</button>
                    </div>
                )}

                {loading && <MDBox py={3}><MDTypography>Loading...</MDTypography></MDBox>}

                {!loading && metrics && !metrics.empty && (
                    <>
                        {/* KPI Cards */}
                        <MDBox mt={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={4}>
                                    <ComplexStatisticsCard color="info" icon="description"
                                        title="Total Documents" count={metrics.total}
                                        percentage={{ color: "text", amount: "", label: `${startDate} → ${endDate}` }} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <ComplexStatisticsCard color="success" icon="stars"
                                        title="Horoscopes" count={metrics.horoscopeCount}
                                        percentage={{ color: "success", amount: "", label: "fileType 1" }} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <ComplexStatisticsCard color="warning" icon="favorite"
                                        title="Matching" count={metrics.matchCount}
                                        percentage={{ color: "warning", amount: "", label: "fileType 2" }} />
                                </Grid>
                            </Grid>
                        </MDBox>

                        {/* Trend Chart */}
                        <MDBox mt={3}>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 4 }}>
                                    <button type="button" style={toggleBtnStyle(trendView === "monthly")} onClick={() => setTrendView("monthly")}>Monthly</button>
                                    <button type="button" style={toggleBtnStyle(trendView === "weekly")}  onClick={() => setTrendView("weekly")}>Weekly</button>
                                </div>
                                <DefaultLineChart
                                    icon={{ component: "show_chart", color: "info" }}
                                    title="Document Creation Trend"
                                    description={trendView === "weekly"
                                        ? `Weekly breakdown (${startDate} → ${endDate})`
                                        : `Monthly breakdown (${startDate} → ${endDate})`}
                                    height="18rem"
                                    chart={{
                                        labels: trendLabels || [],
                                        datasets: [
                                            { label: "Horoscopes", color: "info",    data: trendHoro  || [] },
                                            { label: "Matching",   color: "warning", data: trendMatch || [] },
                                        ],
                                    }}
                                />
                            </div>
                        </MDBox>

                        {/* Monthly breakdown table */}
                        <MDBox mt={3} mb={4}>
                            <Card>
                                <MDBox pt={2} px={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
                                    <MDTypography variant="h6" fontWeight="medium">Monthly Breakdown</MDTypography>
                                    <MDTypography variant="caption" color="text">{startDate} → {endDate}</MDTypography>
                                </MDBox>
                                <MDBox px={2} pb={2}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Month</th>
                                                <th style={thStyle}>Horoscopes</th>
                                                <th style={thStyle}>Matching</th>
                                                <th style={thStyle}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics.monthlyLabels.map((label, idx) => {
                                                const h = metrics.monthlyHoro[idx];
                                                const m = metrics.monthlyMatch[idx];
                                                return (
                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                                        <td style={{ ...cellStyle, fontWeight: 500 }}>{label}</td>
                                                        <td style={{ ...cellStyle, color: '#1565c0' }}>{h}</td>
                                                        <td style={{ ...cellStyle, color: '#e65100' }}>{m}</td>
                                                        <td style={{ ...cellStyle, fontWeight: 'bold' }}>{h + m}</td>
                                                    </tr>
                                                );
                                            })}
                                            <tr style={{ background: '#e8f4fd' }}>
                                                <td style={{ ...cellStyle, fontWeight: 'bold' }}>Total</td>
                                                <td style={{ ...cellStyle, fontWeight: 'bold', color: '#1565c0' }}>{metrics.horoscopeCount}</td>
                                                <td style={{ ...cellStyle, fontWeight: 'bold', color: '#e65100' }}>{metrics.matchCount}</td>
                                                <td style={{ ...cellStyle, fontWeight: 'bold' }}>{metrics.total}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </MDBox>
                            </Card>
                        </MDBox>
                    </>
                )}

                {!loading && metrics?.empty && (
                    <MDBox py={3}><MDTypography color="text">No documents match the current filter.</MDTypography></MDBox>
                )}
                {!loading && rawData.length === 0 && !error && (
                    <MDBox py={3}><MDTypography color="text">No documents found for the selected date range.</MDTypography></MDBox>
                )}
            </div>
        </DashboardLayout>
    );
}

export default DocumentDashboard;
